import 'swiper/css';
import 'swiper/css/scrollbar';

import React from 'react';
import { Scrollbar } from 'swiper';
import Image from 'next/future/image';
import { first, isEmpty } from 'lodash';
import { Swiper, SwiperSlide } from 'swiper/react';

import { IProduct } from '@/models';

import ClampedText from '../../ClampedText';
import { useTranslation } from 'react-i18next';
import VideoPlayer from '../../VideoPlayer';

interface Props {
  product: IProduct;
}

const Technologies: React.FC<Props> = ({ product }) => {
  const { t } = useTranslation();
  const technology = first(product.technologies);
  const technologies = product.technologies.slice(1);
  const video = first(product.technologies.map(({ video }) => video));

  if (!technology) {
    return null;
  }

  return (
    <section className="section full space-y-md bg-sand-100">
      <div className="section-wrapper">
        <div className="h3 text-center mb-12">
          {t('P_PDP.TECHNOLOGIES.TITLE')}
        </div>

        <div className="space-y-8 md:space-y-12 xl:space-y-20">
          <div className="grid grid-cols-1 xl:grid-cols-5 gap-10 md:max-w-screen-xl md:mx-auto">
            <div className="col-span-1 xl:col-span-2 flex items-center">
              <div className="md:cols-span-1 flex flex-col justify-center space-y-6 p-6">
                <div className="space-y-3">
                  {technology.title && (
                    <h3 className="h3">{technology.title}</h3>
                  )}

                  <p className="text-body">{technology.name}</p>
                </div>

                <div
                  className="text-sm tech_formatter"
                  dangerouslySetInnerHTML={{ __html: technology.description }}
                ></div>
              </div>
            </div>
            <div className="col-span-1 xl:col-span-3">
              <Image
                src={technology.image}
                alt={technology.name}
                width={0}
                height={0}
                sizes="100vw"
                className=" w-full h-full xl:w-auto xl:h-[550px] object-cover mx-auto"
              />
            </div>
          </div>

          {video && (
            <div className="video-wrapper aspect-landscape">
              <VideoPlayer src={video} />
            </div>
          )}

          {!isEmpty(technologies) && (
            <Swiper
              wrapperTag="ul"
              slidesPerView={technologies.length > 1 ? 1.5 : 1}
              spaceBetween={10}
              modules={[Scrollbar]}
              scrollbar={{ hide: false }}
              className="swiperScrollbar"
              breakpoints={{
                768: {
                  slidesPerView: technologies.length > 2 ? 2.5 : 2
                },
                1024: {
                  slidesPerView:
                    technologies.length > 3
                      ? 3.5
                      : Math.max(2, technologies.length)
                },
                1280: {
                  slidesPerView:
                    technologies.length > 4
                      ? 4.5
                      : Math.max(3, technologies.length)
                }
              }}
            >
              {technologies.map(technology => (
                <SwiperSlide
                  key={technology.name}
                  tag="li"
                  className="space-y-6"
                >
                  <Image
                    src={technology.image}
                    alt={technology.name}
                    width={0}
                    height={0}
                    sizes="100vw"
                    className="w-full h-auto"
                  />

                  <div className="space-y-3">
                    {technology.title && (
                      <div className="h5">{technology.title}</div>
                    )}

                    <p className="text-body">{technology.name}</p>
                  </div>

                  <ClampedText
                    clamp={3}
                    containerClassName="text-sm"
                    buttonClassName="from-sand-100"
                  >
                    {technology.description}
                  </ClampedText>
                </SwiperSlide>
              ))}
            </Swiper>
          )}
        </div>
      </div>
    </section>
  );
};

export default Technologies;
